import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
  ViewChild,
} from '@angular/core';
import { Observable, switchMap, take } from 'rxjs';
import Plyr from 'plyr';
import { VideoUploadService } from '@arkius/shared-api';
import { environment } from '@shared-envs';
import { AlertController } from '@ionic/angular';

export interface NativeVideoUploadResult {
  url: string;
  status: string | null;
  response: any;
}

export function isSupportedNativeVideoFile(file: File): boolean {
  return (
    ['video/mp4', 'video/quicktime'].includes(file.type.toLowerCase()) ||
    /\.(mp4|mov)$/i.test(file.name)
  );
}

@Component({
  selector: 'ark-lib-native-video-upload',
  templateUrl: './native-video-upload.component.html',
  styleUrls: ['./native-video-upload.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class NativeVideoUploadComponent implements OnDestroy {
  @ViewChild('nativeVideoPreview')
  nativeVideoPreview?: ElementRef<HTMLVideoElement>;
  @ViewChild('nativeVideoFile')
  nativeVideoFile?: ElementRef<HTMLInputElement>;

  @Input() uploadedVideoUrl: string | null = null;
  @Input() uploadedVideoStatus: string | null = null;
  @Input() maxDurationSeconds = 60;
  @Input() showPicker = true;

  @Output() videoUploaded = new EventEmitter<NativeVideoUploadResult>();
  @Output() videoRemoved = new EventEmitter<void>();

  selectedVideo: File | null = null;
  selectedVideoPreviewUrl: string | null = null;
  uploading = false;
  uploadFailed = false;
  uploadErrorMessage = '';
  previewWarningMessage = '';
  uploadResponse: any = null;
  uploadProgress = 0;
  videoDuration: number | null = null;
  durationChecked = false;
  previewFrameReady = false;

  private previewPlayer: Plyr | null = null;

  constructor(
    private videoUploadService: VideoUploadService,
    private changeDetectorRef: ChangeDetectorRef,
    private alertController: AlertController,
  ) {}

  get statusLabel(): string {
    return this.uploadedVideoStatus || this.uploadResponse?.status || 'Processing';
  }

  get selectedVideoTooLong(): boolean {
    return (
      this.videoDuration !== null &&
      Number.isFinite(this.videoDuration) &&
      this.videoDuration > this.maxDurationSeconds
    );
  }

  get blocksSubmission(): boolean {
    return (
      this.uploading ||
      this.uploadFailed ||
      this.selectedVideoTooLong ||
      (!!this.selectedVideo && !this.uploadResponse?.cloudFrontUrl)
    );
  }

  get submissionBlockerMessage(): string {
    if (this.uploading) {
      return 'Please wait for the video upload to finish before submitting.';
    }

    if (this.selectedVideoTooLong) {
      return `Videos must be ${this.maxDurationSeconds} seconds or shorter.`;
    }

    if (this.uploadFailed) {
      return this.uploadErrorMessage || 'Video upload failed. Please remove it or try again.';
    }

    if (this.selectedVideo && !this.uploadResponse?.cloudFrontUrl) {
      return 'Upload the selected video or remove it before submitting.';
    }

    return '';
  }

  showSubmissionBlocker() {
    const message = this.submissionBlockerMessage;
    if (message) {
      this.uploadErrorMessage = message;
    }
  }

  get uploadProgressLabel(): string {
    return `${Math.round(this.uploadProgress)}%`;
  }

  onUploadButtonClick(fileInput?: HTMLInputElement) {
    if (this.uploading) return;

    environment.log('[Native video] Upload picker opened');
    this.uploadErrorMessage = '';
    this.previewWarningMessage = '';
    this.uploadFailed = false;
    fileInput?.click();
  }

  onVideoSelected(event: any) {
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) {
      environment.log('[Native video] No file selected');
      return;
    }

    this.selectVideo(file);
  }

  selectVideo(file: File) {
    if (this.uploading) {
      this.clearFileInput();
      return;
    }

    this.uploadErrorMessage = '';
    this.previewWarningMessage = '';
    this.uploadFailed = false;
    this.uploadResponse = null;
    this.uploadProgress = 0;
    this.videoDuration = null;
    this.durationChecked = false;
    this.previewFrameReady = false;
    this.selectedVideo = null;
    this.clearSelectedVideoPreview();
    this.changeDetectorRef.detectChanges();

    const previewUrl = URL.createObjectURL(file);

    environment.log('[Native video] File selected', {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
      localPreviewUrl: previewUrl,
    });

    if (!isSupportedNativeVideoFile(file)) {
      environment.log('[Native video] Unsupported file type', file.type);
      this.uploadErrorMessage = 'Only MP4 and MOV videos are supported.';
      this.selectedVideo = null;
      URL.revokeObjectURL(previewUrl);
      this.clearFileInput();
      return;
    }

    this.selectedVideo = file;
    this.selectedVideoPreviewUrl = previewUrl;
    environment.log('[Native video] Local preview ready', {
      localPreviewUrl: this.selectedVideoPreviewUrl,
    });
  }

  uploadVideo() {
    if (
      !this.selectedVideo ||
      this.uploading ||
      !this.durationChecked ||
      this.selectedVideoTooLong
    ) {
      return;
    }

    const file = this.selectedVideo;
    const contentType =
      file.type || (/\.mov$/i.test(file.name) ? 'video/quicktime' : 'video/mp4');

    environment.log('[Native video] Upload started', {
      fileName: file.name,
      contentType,
      size: file.size,
      duration: this.videoDuration,
    });

    this.uploading = true;
    this.uploadFailed = false;
    this.uploadProgress = 0;
    this.uploadErrorMessage = '';
    this.previewWarningMessage = '';

    this.videoUploadService
      .apiV5VideoUploadUploadUrlGet(file.name, contentType)
      .pipe(
        take(1),
        switchMap((uploadInfo: any) => {
          this.uploadResponse = uploadInfo;
          environment.log('[Native video] Signed upload URL response', {
            videoId: uploadInfo?.videoId,
            cloudFrontUrl: uploadInfo?.cloudFrontUrl,
            status: uploadInfo?.status,
            hasUploadUrl: !!uploadInfo?.uploadUrl,
          });

          return this.uploadFileToSignedUrl(uploadInfo.uploadUrl, file, contentType);
        }),
      )
      .subscribe({
        next: () => {
          const cloudFrontUrl = this.uploadResponse?.cloudFrontUrl;

          if (!cloudFrontUrl) {
            environment.log('[Native video] Missing cloudFrontUrl after upload');
            this.uploading = false;
            this.uploadFailed = true;
            this.uploadErrorMessage =
              'Upload completed, but the API did not return a playable video URL.';
            return;
          }

          this.uploading = false;
          this.uploadProgress = 100;
          environment.log('[Native video] Upload complete; CloudFront URL emitted', {
            cloudFrontUrl,
            status: this.uploadResponse?.status,
            localPreviewUrl: this.selectedVideoPreviewUrl,
          });

          this.videoUploaded.emit({
            url: cloudFrontUrl,
            status: this.uploadResponse?.status || null,
            response: this.uploadResponse,
          });
        },
        error: (error) => {
          environment.log('[Native video] Upload failed', error);
          this.uploading = false;
          this.uploadFailed = true;
          this.uploadProgress = 0;
          this.uploadErrorMessage = 'Video upload failed. Please try again.';
        },
      });
  }

  cancelUpload() {
    environment.log('[Native video] Upload cancelled');
    this.selectedVideo = null;
    this.uploadErrorMessage = '';
    this.previewWarningMessage = '';
    this.uploadFailed = false;
    this.uploadResponse = null;
    this.uploadProgress = 0;
    this.videoDuration = null;
    this.durationChecked = false;
    this.clearSelectedVideoPreview();
    this.clearFileInput();
  }

  removeUploadedVideo() {
    this.cancelUpload();
    this.videoRemoved.emit();
  }

  async removeCurrentVideo() {
    const confirmed = await this.confirmRemoveVideo();
    if (!confirmed) return;

    if (this.uploadedVideoUrl || this.uploadResponse?.cloudFrontUrl) {
      this.removeUploadedVideo();
      return;
    }

    this.cancelUpload();
  }

  onPreviewLoaded(event: Event) {
    const video = event.target as HTMLVideoElement;
    this.videoDuration = Number.isFinite(video.duration) ? video.duration : null;
    this.durationChecked = true;

    // Android WebView can show its generic video placeholder until a frame is
    // decoded. Seeking just past the start forces it to paint a real preview.
    if (video.duration > 0.1 && video.currentTime === 0) {
      video.currentTime = 0.1;
    }

    environment.log('[Native video] Local preview metadata loaded', {
      duration: video.duration,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      readyState: video.readyState,
      networkState: video.networkState,
      localPreviewUrl: this.selectedVideoPreviewUrl,
    });

    if (this.selectedVideoTooLong) {
      this.uploadErrorMessage = `Videos must be ${this.maxDurationSeconds} seconds or shorter.`;
    }

    this.updatePreviewWarning(video);
    this.initializePreviewPlayer(video);
  }

  onPreviewError(event: Event) {
    const video = event.target as HTMLVideoElement;
    this.durationChecked = true;
    environment.log('[Native video] Local preview error', {
      errorCode: video.error?.code,
      errorMessage: video.error?.message,
      readyState: video.readyState,
      networkState: video.networkState,
      localPreviewUrl: this.selectedVideoPreviewUrl,
    });
  }

  onPreviewState(state: string, event: Event) {
    const video = event.target as HTMLVideoElement;
    environment.log(`[Native video] Local preview ${state}`, {
      duration: video.duration,
      currentTime: video.currentTime,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      readyState: video.readyState,
      networkState: video.networkState,
      paused: video.paused,
      localPreviewUrl: this.selectedVideoPreviewUrl,
    });
    this.updatePreviewWarning(video);

    if (state === 'loadeddata' && video.duration <= 0.1) {
      this.revealPreviewAfterPaint();
    }
  }

  onPreviewFrameReady() {
    this.revealPreviewAfterPaint();
  }

  ngOnDestroy() {
    this.clearSelectedVideoPreview();
  }

  private clearSelectedVideoPreview() {
    this.destroyPreviewPlayer();
    this.previewWarningMessage = '';
    this.previewFrameReady = false;

    if (!this.selectedVideoPreviewUrl) return;

    environment.log('[Native video] Local preview revoked', {
      localPreviewUrl: this.selectedVideoPreviewUrl,
    });
    URL.revokeObjectURL(this.selectedVideoPreviewUrl);
    this.selectedVideoPreviewUrl = null;
  }

  private clearFileInput() {
    if (this.nativeVideoFile?.nativeElement) {
      this.nativeVideoFile.nativeElement.value = '';
    }
  }

  private updatePreviewWarning(video: HTMLVideoElement) {
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      this.previewWarningMessage = '';
      return;
    }

    this.previewWarningMessage =
      'This MP4 has audio, but its video track cannot be previewed in this browser. It can still be uploaded for processing.';
  }

  private initializePreviewPlayer(video: HTMLVideoElement) {
    if (this.previewPlayer) return;

    this.previewPlayer = new Plyr(video, {
      controls: [
        'play-large',
        'play',
        'progress',
        'current-time',
        'mute',
        'volume',
        'fullscreen',
      ],
    });
    this.previewPlayer.elements.container?.classList.add(
      'native-video-preview-player',
      'native-video-preview-pending',
    );

    environment.log('[Native video] Local Plyr preview initializing', {
      localPreviewUrl: this.selectedVideoPreviewUrl,
      readyState: video.readyState,
      networkState: video.networkState,
    });

    this.previewPlayer.on('ready', () => {
      environment.log('[Native video] Local Plyr preview ready', {
        duration: video.duration,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        readyState: video.readyState,
        networkState: video.networkState,
        localPreviewUrl: this.selectedVideoPreviewUrl,
      });
    });
  }

  private async confirmRemoveVideo(): Promise<boolean> {
    const alert = await this.alertController.create({
      header: 'Remove Video',
      message: 'Are you sure you want to remove this video?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Remove',
          role: 'confirm',
        },
      ],
    });

    await alert.present();
    const result = await alert.onWillDismiss();

    return result.role === 'confirm';
  }

  private uploadFileToSignedUrl(
    uploadUrl: string,
    file: File,
    contentType: string,
  ): Observable<void> {
    return new Observable((observer) => {
      const xhr = new XMLHttpRequest();

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;

        this.uploadProgress = Math.min(
          99,
          Math.round((event.loaded / event.total) * 100),
        );
        this.changeDetectorRef.detectChanges();

        environment.log('[Native video] S3 PUT progress', {
          loaded: event.loaded,
          total: event.total,
          progress: this.uploadProgress,
        });
      };

      xhr.onload = () => {
        environment.log('[Native video] S3 PUT response', {
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          statusText: xhr.statusText,
        });

        if (xhr.status < 200 || xhr.status >= 300) {
          observer.error(new Error(`Video upload failed with ${xhr.status}`));
          return;
        }

        this.uploadProgress = 100;
        this.changeDetectorRef.detectChanges();
        observer.next();
        observer.complete();
      };

      xhr.onerror = () => {
        observer.error(new Error('Video upload failed due to a network error.'));
      };

      xhr.onabort = () => {
        observer.error(new Error('Video upload was aborted.'));
      };

      xhr.send(file);

      return () => {
        if (xhr.readyState !== XMLHttpRequest.DONE) {
          xhr.abort();
        }
      };
    });
  }

  private destroyPreviewPlayer() {
    if (!this.previewPlayer) return;

    environment.log('[Native video] Local Plyr preview destroyed');
    this.previewPlayer.destroy();
    this.previewPlayer = null;
  }

  private revealPreviewAfterPaint() {
    if (this.previewFrameReady) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.previewFrameReady = true;
        const previewElement =
          this.previewPlayer?.elements.container ??
          this.nativeVideoPreview?.nativeElement;

        previewElement?.classList.remove('native-video-preview-pending');
        this.nativeVideoPreview?.nativeElement.focus({ preventScroll: true });
        previewElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    });
  }
}
